import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ShieldAlert } from "lucide-react";

import { useAssetLookup } from "../api/useAssetLookup";
import { AssetIcon } from "../components/AssetIcon";
import { formatGameTime } from "../format/formatGameTime";
import { formatPercent } from "../format/formatPercent";
import { AssetList } from "./AssetList";
import { FindingCard } from "./FindingCard";
import type { FindingProps } from "./FindingProps";
import { iconSize } from "./iconSize";
import { IconBadge } from "./IconBadge";

const TITLE_LATE = msg({
  id: "finding.defense_mismatch.late",
  message:
    "{kind, select, bullet {Bullet} spirit {Spirit} other {Melee}} resistance {minutes, plural, one {# minute} other {# minutes}} late",
});

const TITLE_ONLY_AT = msg({
  id: "finding.defense_mismatch.only_at",
  message:
    "{kind, select, bullet {Bullet} spirit {Spirit} other {Melee}} resistance only at {time}",
});

const TITLE_MISSING = msg({
  id: "finding.defense_mismatch.missing",
  message: "No {kind, select, bullet {bullet} spirit {spirit} other {melee}} resistance",
});

const SHARE = msg({
  id: "finding.defense_mismatch.share",
  message:
    "{share} of the damage you took was {kind, select, bullet {bullets} spirit {spirit} other {melee}}.",
});

const BOUGHT = msg({
  id: "finding.defense_mismatch.bought",
  message:
    "Your first resistance against it was {item} at {bought}; strong players on your hero take it by {baseline}.",
});

const BOUGHT_NO_BASELINE = msg({
  id: "finding.defense_mismatch.bought_no_baseline",
  message: "Your first resistance against it was {item} at {bought}.",
});

const NOT_BY = msg({
  id: "finding.defense_mismatch.not_by",
  message: "By {time} you had no resistance against it.",
});

const SUMMARY_BOUGHT = msg({
  id: "finding.defense_mismatch.summary_bought",
  message: "{item} at {bought}, usually by {baseline}",
});

const SUGGESTED = msg({
  id: "finding.suggested_items",
  message: "Strong players on your hero take:",
});

// Урон по игроку в основном одного типа, а защиты от него не было вовремя. Купил позже —
// какую защиту и когда, против времени сильных игроков; не купил — что берут сильные.
export const DefenseMismatch = ({ finding, variant }: FindingProps<"defense_mismatch">) => {
  const { i18n } = useLingui();
  const findItem = useAssetLookup("items");
  const {
    damage_kind: kind,
    share,
    checked_at_s: checkedAt,
    bought_item_id: boughtId,
    bought_at_s: boughtAt,
    baseline_at_s: baseline,
  } = finding.params;
  const shareText = i18n._({
    ...SHARE,
    values: { share: formatPercent(share, i18n.locale), kind },
  });
  const bought = boughtId != null && boughtAt != null ? findItem(boughtId) : null;
  const late = bought !== null && boughtAt != null && baseline != null && boughtAt > baseline;
  const times = {
    item: bought?.name ?? "",
    bought: formatGameTime(boughtAt ?? 0),
    baseline: formatGameTime(baseline ?? 0),
  };
  const title = late
    ? i18n._({
        ...TITLE_LATE,
        values: { kind, minutes: Math.max(Math.round((boughtAt - baseline) / 60), 1) },
      })
    : bought !== null
      ? i18n._({ ...TITLE_ONLY_AT, values: { kind, time: times.bought } })
      : i18n._({ ...TITLE_MISSING, values: { kind } });
  const when =
    bought === null
      ? i18n._({ ...NOT_BY, values: { time: formatGameTime(checkedAt) } })
      : i18n._({ ...(baseline == null ? BOUGHT_NO_BASELINE : BOUGHT), values: times });
  const details = `${shareText} ${when}`;
  return (
    <FindingCard
      code={finding.code}
      severity={finding.severity}
      variant={variant}
      icon={
        bought !== null ? (
          <AssetIcon asset={bought} size={iconSize(variant)} />
        ) : (
          <IconBadge icon={ShieldAlert} size={iconSize(variant)} />
        )
      }
      title={title}
      details={details}
      summary={late ? i18n._({ ...SUMMARY_BOUGHT, values: times }) : shareText}
    >
      {bought === null && (
        <AssetList
          label={i18n._(SUGGESTED)}
          entries={finding.params.suggested_item_ids.map((id) => ({ asset: findItem(id) }))}
        />
      )}
    </FindingCard>
  );
};
